import { useLayoutEffect, useRef } from "react";
import * as THREE from "three";
import { OBJLoader, STLLoader } from "three-stdlib";
import { attachUnparentedSitesToModelGroup, processBodyFromStore } from "../processBodyFromStore";
import { createJointVisualization } from "../jointVisualization";
import { getSceneState } from "../../stores/sceneStore";
import { getViewerActions } from "../../stores/viewerStore";
import * as RuntimeRegistry from "../../runtime/objectRegistry";
import { applyJointValue } from "../kinematics";
import { clearViewportTransientOverlays } from "../sceneViewportCleanup";
import { releaseAndDisposeSceneObjectTree } from "../scene/sceneDispose";
import { devLog } from "../scene/sceneConstants";
import { safeTraverse } from "../scene/sceneObjectLookups";
import { logger } from "../../utils/logger";
import { clearSceneForEmptyModel } from "./scene-rebuild/sceneRebuildClear";
import {
  hidePosPickMarker,
  publishSceneObjects,
  registerPendingMeshLoadFactory,
  scheduleJointMarkerSizing,
  shouldUseStructureSignatureFastPath,
  syncBodyTransformsFromStore,
  updateSceneRebuildRefs,
} from "./scene-rebuild/sceneRebuildShared";
import { applyInPlaceMeshSwaps } from "./scene-rebuild/sceneRebuildMeshSwaps";
import {
  logKinematicsAfterRebuild,
  logParentingAfterRebuild,
  logRootsAfterRebuild,
} from "./scene-rebuild/sceneRebuildDebug";

export function useSceneRebuild({
  bodyTree,
  rebuildBodyTree,
  hasWorldLevelSitesForRebuild,
  fileMap,
  getSortedFileKeys,
  getRootUuids,
  collectDescendantsByUuid,
  previousFileMapKeysRef,
  computeFileMapSignature,
  fileMapSignatureRef,
  rebuildStructureSig,
  computeBodiesStructureSignature,
  safeJointMapObject,
  jointKindsSignature,
  structureChangeTick,
  lastHandledStructureChangeTickRef,
  bodyTreeStructureSignatureRef,
  sceneRef,
  sceneObjectsRef,
  jointObjectsRef,
  siteLabelsRef,
  jointLabelRefs,
  modelGroupRef,
  sceneBuildTokenRef,
  removedBodyCacheRef,
  autoFitCameraRef,
  initialBuildDoneRef,
  previousBodiesRef,
  registerPendingBodyMeshLoad,
  setSceneObjects,
  applyJointMarkerSizes,
  jointMarkerScalePercent,
  jointMarkerSizes,
  isPhysicsRunning,
  getJointSnapshot,
  sceneActions,
  isHistoryRestoringRef,
  useYUp,
  showJoints,
  showCollision,
  showVisual,
  showSites,
  showInertials,
  materials,
}) {
  const rebuildInputsRef = useRef(null);
  rebuildInputsRef.current = {
    bodyTree,
    getSortedFileKeys,
    getRootUuids,
    collectDescendantsByUuid,
    previousFileMapKeysRef,
    computeFileMapSignature,
    fileMapSignatureRef,
    computeBodiesStructureSignature,
    safeJointMapObject,
    lastHandledStructureChangeTickRef,
    bodyTreeStructureSignatureRef,
    sceneRef,
    sceneObjectsRef,
    jointObjectsRef,
    siteLabelsRef,
    jointLabelRefs,
    modelGroupRef,
    sceneBuildTokenRef,
    removedBodyCacheRef,
    autoFitCameraRef,
    initialBuildDoneRef,
    previousBodiesRef,
    registerPendingBodyMeshLoad,
    setSceneObjects,
    applyJointMarkerSizes,
    jointMarkerScalePercent,
    jointMarkerSizes,
    isPhysicsRunning,
    getJointSnapshot,
    sceneActions,
    isHistoryRestoringRef,
    showJoints,
    showCollision,
    showVisual,
    showSites,
    showInertials,
    materials,
  };

  // Separate effect for robot loading
  useLayoutEffect(() => {
    const {
      bodyTree,
      getSortedFileKeys,
      getRootUuids,
      collectDescendantsByUuid,
      previousFileMapKeysRef,
      computeFileMapSignature,
      fileMapSignatureRef,
      computeBodiesStructureSignature,
      safeJointMapObject,
      lastHandledStructureChangeTickRef,
      bodyTreeStructureSignatureRef,
      sceneRef,
      sceneObjectsRef,
      jointObjectsRef,
      siteLabelsRef,
      jointLabelRefs,
      modelGroupRef,
      sceneBuildTokenRef,
      removedBodyCacheRef,
      autoFitCameraRef,
      initialBuildDoneRef,
      previousBodiesRef,
      registerPendingBodyMeshLoad,
      setSceneObjects,
      applyJointMarkerSizes,
      jointMarkerScalePercent,
      jointMarkerSizes,
      isPhysicsRunning,
      getJointSnapshot,
      sceneActions,
      isHistoryRestoringRef,
      showJoints,
      showCollision,
      showVisual,
      showSites,
      showInertials,
      materials,
    } = rebuildInputsRef.current;

    // Worldbody-level sites are valid MJCF scene contents even when there are no body nodes.
    const modelLoaded = rebuildBodyTree.length > 0 || hasWorldLevelSitesForRebuild;
    // hasUploadedFiles removed - only XML-based robot loading is supported
    const hasXmlFileInMap = fileMap && Object.keys(fileMap).some((name) => name.endsWith(".xml"));
    const currentFileKeys = getSortedFileKeys(fileMap);
    const previousFileKeys = previousFileMapKeysRef.current || [];
    const newFileMapSig = computeFileMapSignature(fileMap);
    const assetsChanged = newFileMapSig !== fileMapSignatureRef.current;
    const assetsAddedOnly =
      assetsChanged &&
      previousFileKeys.length > 0 &&
      previousFileKeys.every((key) => currentFileKeys.includes(key));
    const allowAssetChange = !assetsChanged || assetsAddedOnly;

    // Removed early skip-rebuild path to ensure full rebuild happens when needed

    // Compute structure signature to differentiate structural vs non-structural changes
    const newStructureSig =
      rebuildStructureSig || computeBodiesStructureSignature(rebuildBodyTree, safeJointMapObject);
    const sawStructureChangeSignal =
      typeof structureChangeTick === "number" &&
      structureChangeTick !== lastHandledStructureChangeTickRef.current;

    if (modelLoaded && !bodyTreeStructureSignatureRef.current) {
      bodyTreeStructureSignatureRef.current = newStructureSig;
    }

    // Also check if we need to clear the scene (empty project)
    const needsSceneClear = !modelLoaded && sceneRef.current;

    if (!sceneRef.current) {
      return;
    }

    // If model is loaded and structure hasn't changed, update transforms in-place and skip rebuild
    if (modelLoaded) {
      const prevSig = bodyTreeStructureSignatureRef.current;
      const existingModel = sceneRef.current.getObjectByName("modelGroup");
      if (
        shouldUseStructureSignatureFastPath({
          allowAssetChange,
          existingModel,
          nextStructureSignature: newStructureSig,
          previousStructureSignature: prevSig,
          sawStructureChangeSignal,
        })
      ) {
        const objectsMap = sceneObjectsRef.current || {};
        // When physics is running, poses are driven by the worker; don't overwrite them
        // with store-based kinematic transforms.
        if (!isPhysicsRunning) {
          syncBodyTransformsFromStore({
            getJointSnapshot,
            jointObjectsRef,
            objectsMap,
            sceneActions,
            warningMessage: "[Scene] Failed to detect kinematic control for body in fast-path sync",
          });
        }

        // Diff mesh instances (meshId/mesh name) and apply in-place swaps
        // Gate: only run after the first full build completes to avoid initial-load flicker
        if (initialBuildDoneRef.current) {
          applyInPlaceMeshSwaps({
            bodyTree,
            fileMap,
            materials,
            objectsMap,
            previousBodies: previousBodiesRef.current,
          });
        }
        // Keep signature up to date and bail out to avoid flicker
        updateSceneRebuildRefs({
          bodyTree,
          bodyTreeStructureSignatureRef,
          currentFileKeys,
          fileMapSignatureRef,
          newFileMapSig,
          newStructureSig,
          previousBodiesRef,
          previousFileMapKeysRef,
        });
        return;
      }

      if (
        existingModel &&
        prevSig &&
        prevSig !== newStructureSig &&
        allowAssetChange &&
        !sawStructureChangeSignal &&
        previousBodiesRef.current &&
        previousBodiesRef.current.length > 0
      ) {
        const previousRootIds = new Set(getRootUuids(previousBodiesRef.current));
        const currentRootIds = new Set(getRootUuids(bodyTree));
        const addedRootNodes = bodyTree.filter(
          (node) => node?.uuid && !previousRootIds.has(node.uuid)
        );
        const removedRootNodes = (previousBodiesRef.current || []).filter(
          (node) => node?.uuid && !currentRootIds.has(node.uuid)
        );

        if (addedRootNodes.length > 0 && removedRootNodes.length === 0) {
          const loader = new OBJLoader();
          const stlLoader = new STLLoader();
          const modelGroup = existingModel;
          const pendingMeshLoads = [];
          const registerPendingMeshLoad = registerPendingMeshLoadFactory(
            registerPendingBodyMeshLoad,
            pendingMeshLoads
          );
          const processOptions = {
            sceneActions,
            materials,
            fileMap,
            loader,
            stlLoader,
            sceneObjectsRef,
            jointObjectsRef,
            siteLabelsRef,
            showJoints,
            showCollision,
            showVisual,
            showSites,
            showInertials,
            jointMarkerSizes,
            sceneRef,
            registerPendingMeshLoad,
          };

          addedRootNodes.forEach((body) => {
            processBodyFromStore(body, modelGroup, undefined, undefined, processOptions);
          });

          const hadPendingMeshLoads = pendingMeshLoads.length > 0;
          if (hadPendingMeshLoads) {
            Promise.allSettled(pendingMeshLoads).then(() => {
              setSceneObjects({ ...sceneObjectsRef.current });
              try {
                RuntimeRegistry.setAll(sceneObjectsRef.current);
              } catch (error) {
                logger.warn("[Scene] Unexpected error during scene update", error);
              }
            });
          }

          publishSceneObjects(sceneObjectsRef, setSceneObjects);

          updateSceneRebuildRefs({
            bodyTree,
            bodyTreeStructureSignatureRef,
            currentFileKeys,
            fileMapSignatureRef,
            initialBuildDoneRef,
            newFileMapSig,
            newStructureSig,
            previousBodiesRef,
            previousFileMapKeysRef,
          });

          scheduleJointMarkerSizing({
            applyJointMarkerSizes,
            jointMarkerScalePercent,
            newFileMapSig,
            newStructureSig,
            sceneRef,
          });

          return;
        }
      }

      // If structure changed, attempt in-place diff for deletions/reparenting/undo-deletions
      if (existingModel && prevSig && prevSig !== newStructureSig && !sawStructureChangeSignal) {
        const prevTree = previousBodiesRef.current || [];
        // Helpers to build maps
        const getNodeKey = (node) => {
          const key = node?.uuid || "";
          if (!key) {
            logger.error("[Scene] Body node missing UUID; name-based keys are deprecated.");
          }
          return key;
        };
        const listBodies = (nodes, arr = []) => {
          (nodes || []).forEach((n) => {
            const key = getNodeKey(n);
            if (!key) return;
            arr.push(key);
            if (n.children && n.children.length > 0) listBodies(n.children, arr);
          });
          return arr;
        };
        const parentMapOf = (nodes, map = {}, parentKey = null) => {
          (nodes || []).forEach((n) => {
            const key = getNodeKey(n);
            if (!key) return;
            map[key] = parentKey;
            if (n.children && n.children.length > 0) parentMapOf(n.children, map, key);
          });
          return map;
        };
        const prevKeys = new Set(listBodies(prevTree));
        const newKeys = new Set(listBodies(bodyTree));
        const prevParentMap = parentMapOf(prevTree, {}, null);
        const newParentMap = parentMapOf(bodyTree, {}, null);

        const removed = [...prevKeys].filter((n) => !newKeys.has(n));
        const added = [...newKeys].filter((n) => !prevKeys.has(n));
        const common = [...newKeys].filter((n) => prevKeys.has(n));

        const getDescendantsForKey = (key) => collectDescendantsByUuid(key);

        const objectsMap = sceneObjectsRef.current || {};

        // First restore added bodies from cache (supports undo of deletions)
        let allAddsFromCache = true;
        added.forEach((key) => {
          const cached = removedBodyCacheRef.current[key];
          if (cached) {
            const targetParent = newParentMap[key] ? objectsMap[newParentMap[key]] : existingModel;
            if (targetParent) {
              targetParent.add(cached);
              objectsMap[key] = cached;
              delete removedBodyCacheRef.current[key];
              // Reset joint baselines for restored body and descendants
              const keysToReset = [key, ...getDescendantsForKey(key)];
              keysToReset.forEach((bn) => {
                const entryNames = Object.keys(jointObjectsRef.current || {}).filter((jn) => {
                  const entry = jointObjectsRef.current[jn];
                  if (!entry || !entry.bodyGroup) return false;
                  const bodyGroupUuid = entry.bodyGroup.userData?.bodyUuid;
                  const bodyGroupName = entry.bodyGroup.name;
                  return (
                    (bodyGroupUuid && bodyGroupUuid === bn) ||
                    (bodyGroupName && bodyGroupName === bn)
                  );
                });
                const obj = objectsMap[bn];
                entryNames.forEach((jn) => {
                  if (obj) {
                    jointObjectsRef.current[jn].initialPos = obj.position.clone();
                    jointObjectsRef.current[jn].initialRot = obj.quaternion.clone();
                  }
                });
              });
            }
          } else {
            allAddsFromCache = false;
          }
        });

        // Then reparent bodies whose parent changed
        common.forEach((key) => {
          const obj = objectsMap[key];
          if (!obj) return;
          const oldParentKey = prevParentMap[key];
          const newParentKey = newParentMap[key];
          if (oldParentKey !== newParentKey) {
            const targetParent = newParentKey ? objectsMap[newParentKey] : existingModel;
            if (obj.parent !== targetParent && targetParent) {
              if (obj.parent) obj.parent.remove(obj);
              targetParent.add(obj);
              // Reset joint baselines for this body and its descendants after reparent
              const keysToReset = [key, ...getDescendantsForKey(key)];
              keysToReset.forEach((bn) => {
                const entryNames = Object.keys(jointObjectsRef.current || {}).filter((jn) => {
                  const entry = jointObjectsRef.current[jn];
                  if (!entry || !entry.bodyGroup) return false;
                  const bodyGroupUuid = entry.bodyGroup.userData?.bodyUuid;
                  const bodyGroupName = entry.bodyGroup.name;
                  return (
                    (bodyGroupUuid && bodyGroupUuid === bn) ||
                    (bodyGroupName && bodyGroupName === bn)
                  );
                });
                const objBn = objectsMap[bn];
                entryNames.forEach((jn) => {
                  if (objBn) {
                    jointObjectsRef.current[jn].initialPos = objBn.position.clone();
                    jointObjectsRef.current[jn].initialRot = objBn.quaternion.clone();
                  }
                });
              });
            }
          }
        });

        // Finally, detach and cache removed bodies
        const allRemovalsHandled = true;
        removed.forEach((key) => {
          const obj = objectsMap[key];
          if (!obj) return; // Already absent in scene
          removedBodyCacheRef.current[key] = obj;
          if (obj.parent) {
            // Keep render resources alive while cached so undo can reattach this subtree.
            obj.parent.remove(obj);
          }
          delete objectsMap[key];
        });

        // If we managed removals and any additions came from cache, we can commit in-place
        if (allRemovalsHandled && (added.length === 0 || allAddsFromCache)) {
          // Update transforms for all present bodies to reflect store values
          syncBodyTransformsFromStore({
            getJointSnapshot,
            jointObjectsRef,
            objectsMap,
            sceneActions,
            warningMessage: "[Scene] Failed to detect kinematic control for body in structure sync",
          });

          // Sync joint visualizations for newly added or removed joints
          try {
            const currentJointsState = getSceneState().joints || {};
            // Build a map of jointUuid -> parentBodyUuid from the current body tree
            const treeJointToBody = {};
            const collectTreeJoints = (nodes) => {
              for (const node of nodes || []) {
                const bodyUuid = node?.uuid;
                if (!bodyUuid) continue;
                (Array.isArray(node.joints) ? node.joints : []).forEach((entry) => {
                  const uuid = typeof entry === "string" ? entry : entry?.uuid || null;
                  if (uuid) treeJointToBody[uuid] = bodyUuid;
                });
                if (node.children) collectTreeJoints(node.children);
              }
            };
            collectTreeJoints(bodyTree);

            // Add visualizations for joints that are new or whose kind changed
            for (const [jointUuid, bodyUuid] of Object.entries(treeJointToBody)) {
              const jointInfo = currentJointsState[jointUuid];
              if (!jointInfo) continue;
              const poseGroup = objectsMap[bodyUuid];
              if (!poseGroup) continue;
              const bodyContainer = poseGroup.parent || poseGroup;

              const existingEntry = jointObjectsRef.current[jointUuid];
              const kindChanged = existingEntry && existingEntry.joint?.kind !== jointInfo.kind;

              if (existingEntry && !kindChanged) continue;

              // Remove stale visualization when the kind changed
              if (kindChanged) {
                const toRemove = [];
                safeTraverse(bodyContainer, (obj) => {
                  if (obj.userData?.isJointVizGroup && obj.userData.jointUuid === jointUuid) {
                    toRemove.push(obj);
                  }
                });
                toRemove.forEach((vizObj) => {
                  releaseAndDisposeSceneObjectTree(vizObj);
                  if (vizObj.parent) vizObj.parent.remove(vizObj);
                });
              }

              poseGroup.updateWorldMatrix(true, false);
              const accumulatedRot = new THREE.Quaternion();
              poseGroup.getWorldQuaternion(accumulatedRot);

              const jointViz = createJointVisualization(
                jointInfo,
                accumulatedRot,
                new THREE.Quaternion(),
                poseGroup.position.clone(),
                showJoints,
                jointMarkerSizes,
                sceneActions
              );
              if (jointViz) {
                const jointLocalPos = Array.isArray(jointInfo?.position)
                  ? jointInfo.position
                  : [0, 0, 0];
                const jointOffsetParent = new THREE.Vector3(...jointLocalPos).applyQuaternion(
                  poseGroup.quaternion
                );
                jointViz.position.copy(poseGroup.position.clone().add(jointOffsetParent));
                jointViz.quaternion.copy(poseGroup.quaternion);
                bodyContainer.add(jointViz);
              }

              const parentRot = accumulatedRot
                .clone()
                .multiply(poseGroup.quaternion.clone().invert());
              jointObjectsRef.current[jointUuid] = {
                bodyGroup: poseGroup,
                initialRot: poseGroup.quaternion.clone(),
                initialPos: poseGroup.position.clone(),
                parentRot,
                joint: jointInfo,
                jointUuid,
                jointName: jointInfo.name,
                jointVizGroup: jointViz || null,
              };
            }

            // Remove visualizations for joints no longer in the tree
            const treeJointUuids = new Set(Object.keys(treeJointToBody));
            for (const uuid of Object.keys(jointObjectsRef.current)) {
              if (treeJointUuids.has(uuid)) continue;
              const entry = jointObjectsRef.current[uuid];
              // If a removed joint left a kinematics pivot behind, normalize immediately.
              // Otherwise later local transform sync can apply under the stale pivot parent
              // and visually "spread" links until physics start normalizes pivots.
              if (entry?.bodyGroup?.userData?._pivotGroup) {
                try {
                  const bodyGroup = entry.bodyGroup;
                  const pivot = bodyGroup.userData._pivotGroup;
                  const newParent = pivot?.parent || null;
                  if (newParent) {
                    const worldPos = new THREE.Vector3();
                    const worldQuat = new THREE.Quaternion();
                    bodyGroup.getWorldPosition(worldPos);
                    bodyGroup.getWorldQuaternion(worldQuat);
                    newParent.add(bodyGroup);
                    newParent.remove(pivot);
                    delete bodyGroup.userData._pivotGroup;
                    delete bodyGroup.userData._pivotLocal;
                    const localPos = worldPos.clone();
                    if (typeof newParent.worldToLocal === "function") {
                      newParent.worldToLocal(localPos);
                    }
                    const parentWorldQuat = new THREE.Quaternion();
                    if (typeof newParent.getWorldQuaternion === "function") {
                      newParent.getWorldQuaternion(parentWorldQuat);
                    }
                    const localQuat = parentWorldQuat.clone().invert().multiply(worldQuat);
                    bodyGroup.position.copy(localPos);
                    bodyGroup.quaternion.copy(localQuat);
                  }
                } catch (pivotCleanupError) {
                  logger.warn(
                    "[Scene] Failed to normalize pivot while removing joint",
                    uuid,
                    pivotCleanupError
                  );
                }
              }
              if (entry?.bodyGroup) {
                const toRemove = [];
                const searchRoot = entry.bodyGroup.parent || entry.bodyGroup;
                safeTraverse(searchRoot, (obj) => {
                  if (
                    obj.userData?.isJointVizGroup &&
                    (obj.userData.jointUuid === uuid || obj.userData.jointName === entry.jointName)
                  ) {
                    toRemove.push(obj);
                  }
                });
                toRemove.forEach((obj) => {
                  releaseAndDisposeSceneObjectTree(obj);
                  if (obj.parent) obj.parent.remove(obj);
                });
              }
              delete jointObjectsRef.current[uuid];
            }

            const staleJointVizGroups = [];
            safeTraverse(existingModel, (obj) => {
              if (!obj?.userData?.isJointVizGroup) return;
              const jointUuid = obj.userData.jointUuid;
              if (!jointUuid || treeJointUuids.has(jointUuid)) return;
              staleJointVizGroups.push(obj);
            });
            staleJointVizGroups.forEach((obj) => {
              releaseAndDisposeSceneObjectTree(obj);
              if (obj.parent) obj.parent.remove(obj);
            });
          } catch (e) {
            logger.warn("[Scene] Failed to sync joint visualizations in-place:", e);
          }

          // Commit sceneObjects
          sceneObjectsRef.current = objectsMap;
          publishSceneObjects(sceneObjectsRef, setSceneObjects);

          updateSceneRebuildRefs({
            bodyTree,
            bodyTreeStructureSignatureRef,
            currentFileKeys,
            fileMapSignatureRef,
            newFileMapSig,
            newStructureSig,
            previousBodiesRef,
            previousFileMapKeysRef,
          });
          return;
        }
      }
    }

    // If we have no files and no scene objects, clear the scene
    if (needsSceneClear) {
      clearSceneForEmptyModel({
        currentFileKeys,
        fileMapSignatureRef,
        jointLabelRefs,
        newFileMapSig,
        previousFileMapKeysRef,
        removedBodyCacheRef,
        sceneObjectsRef,
        sceneRef,
        setSceneObjects,
        siteLabelsRef,
      });
      return;
    }

    // If we have no model loaded, return early
    if (!modelLoaded) {
      return;
    }

    // Check if there's an XML file in the fileMap (computed above)
    // const hasXmlFileInMap = fileMap && Object.keys(fileMap).some((name) => name.endsWith(".xml"));

    // Handle new project with uploaded files (no model loaded and no XML file)
    if (!modelLoaded && !hasXmlFileInMap && !isHistoryRestoringRef.current) {
      // Upload handling code removed - only XML-based robot loading is supported
      return;
    }

    // --- Build scene from store state ---
    if (modelLoaded) {
      // Focus logs: body tree and joint alignment
      // Focus logs: show body tree length
      devLog("[Scene] bodyTree length:", bodyTree.length);

      // Upload check removed — only XML-based robot loading is supported.
      clearViewportTransientOverlays(sceneRef.current);

      // Clear existing model
      const existingModel = sceneRef.current.getObjectByName("modelGroup");
      if (existingModel) {
        releaseAndDisposeSceneObjectTree(existingModel);
        sceneRef.current.remove(existingModel);
      }
      Object.values(removedBodyCacheRef.current || {}).forEach((obj) => {
        releaseAndDisposeSceneObjectTree(obj);
      });
      removedBodyCacheRef.current = {};

      // Clear refs
      jointLabelRefs.current = {};
      siteLabelsRef.current = {};

      // Mark that a camera fit should happen after the first mesh loads into this new scene
      autoFitCameraRef.current = true;

      // Hide position pick marker and deactivate pick mode when scene is rebuilt
      hidePosPickMarker(sceneRef.current);

      const loader = new OBJLoader();
      const stlLoader = new STLLoader();

      const modelGroup = new THREE.Group();
      modelGroup.name = "modelGroup";
      if (!useYUp) {
        modelGroup.rotation.x = -Math.PI / 2;
      }
      // Enable model group to cast shadows and reflections
      modelGroup.castShadow = true;
      modelGroup.receiveShadow = true;
      sceneRef.current.add(modelGroup);
      modelGroupRef.current = modelGroup;

      // Build scene from robot store state
      // devLog("[Scene] Building scene from store state:", { bodyTree, joints, materials });

      // Calculate mass range from all inertia data for color scaling
      const calculateMassRange = () => {
        const masses = [];
        const collectMasses = (nodes) => {
          nodes.forEach((node) => {
            if (node.inertia && node.inertia.mass) {
              masses.push(node.inertia.mass);
            }
            if (node.children && node.children.length > 0) collectMasses(node.children);
          });
        };
        collectMasses(bodyTree);
        if (masses.length > 0) {
          const minMass = Math.min(...masses);
          const maxMass = Math.max(...masses);
          getViewerActions().setInertiaMassRange(minMass, maxMass);
          devLog(`[Scene] Set inertia mass range: ${minMass} to ${maxMass}`);
        }
      };

      // Calculate and set mass range for inertia color scaling
      calculateMassRange(bodyTree);

      // Debug: Log the first body's position data
      if (bodyTree.length > 0) {
        devLog("[Scene] First body data:", {
          name: bodyTree[0].name,
          pos: bodyTree[0].position,
          quat: bodyTree[0].orientation,
          geoms: bodyTree[0].geoms,
        });
        devLog("[Scene] Full first body object:", bodyTree[0]);
      }

      // Use imported processBodyFromStore function
      devLog(
        `[Scene] Available materials:`,
        Object.values(materials).map((mat) => mat?.name || mat?.uuid)
      );
      const sceneBuildToken = sceneBuildTokenRef.current + 1;
      sceneBuildTokenRef.current = sceneBuildToken;
      const pendingMeshLoads = [];
      const registerPendingMeshLoad = registerPendingMeshLoadFactory(
        registerPendingBodyMeshLoad,
        pendingMeshLoads
      );
      if (typeof window !== "undefined") {
        window.__critterSceneAssetsLoading = true;
        window.__critterSceneBuildToken = sceneBuildToken;
      }
      const processBodyFromStoreOptions = {
        sceneActions,
        materials,
        fileMap,
        loader,
        stlLoader,
        sceneObjectsRef,
        jointObjectsRef,
        siteLabelsRef,
        showJoints,
        showCollision,
        showVisual,
        showSites,
        showInertials,
        jointMarkerSizes,
        sceneRef,
        registerPendingMeshLoad,
      };

      // Clear joint objects before building new scene
      jointObjectsRef.current = {};
      devLog("[Scene] Cleared joint objects before building scene");

      // IMPORTANT:
      // Treat `body.position/orientation` as the source of truth during rebuild.
      // `state.bodyPositions/bodyRotations` are redundant caches; if they drift (e.g. from prior
      // physics pose application), preferring them can rebuild long kinematic chains "pulled apart"
      // (angles look right, but link spacing is wrong). The body slices already carry the latest
      // transforms, so avoid overriding from the caches here.
      const enrichedBodies = bodyTree;

      // Process all bodies in the enriched body tree
      // devLog(`[Scene] Processing ${enrichedBodies.length} bodies in enriched body tree`);
      devLog(`[Scene] Body tree structure:`, enrichedBodies);

      // Optional debug: root count sanity after rebuild.
      logRootsAfterRebuild(bodyTree);

      // Optional debug: verify Three.js parenting matches store parenting.
      logParentingAfterRebuild({ bodyTree, jointObjectsRef });

      enrichedBodies.forEach((body) => {
        // devLog(`[Scene] About to process body ${index}:`, body.name);
        processBodyFromStore(body, modelGroup, undefined, undefined, processBodyFromStoreOptions);
        // devLog(`[Scene] Finished processing body ${index}:`, body.name);
      });
      attachUnparentedSitesToModelGroup(modelGroup, processBodyFromStoreOptions);
      const hadPendingMeshLoads = pendingMeshLoads.length > 0;
      const finalizeSceneAssetLoad = () => {
        if (sceneBuildTokenRef.current !== sceneBuildToken) return;
        if (typeof window !== "undefined") {
          window.__critterSceneAssetsLoading = false;
          window.__critterSceneBuildToken = sceneBuildToken;
          window.dispatchEvent(
            new CustomEvent("scene-build-assets-complete", {
              detail: {
                buildToken: sceneBuildToken,
                pendingMeshLoads: pendingMeshLoads.length,
              },
            })
          );
        }
        // Mesh collision geoms attach inside async `acquireMeshFromCache` callbacks; the initial
        // `setSceneObjects` below runs before those meshes exist, so hooks that traverse the tree
        // (e.g. collision color-by-kind) must run again after assets land.
        if (hadPendingMeshLoads) {
          setSceneObjects({ ...sceneObjectsRef.current });
          try {
            RuntimeRegistry.setAll(sceneObjectsRef.current);
          } catch (error) {
            logger.warn("[Scene] Unexpected error during scene update", error);
          }
        }
      };
      if (pendingMeshLoads.length > 0) {
        Promise.allSettled(pendingMeshLoads)
          .then(finalizeSceneAssetLoad)
          .catch(finalizeSceneAssetLoad);
      } else {
        setTimeout(finalizeSceneAssetLoad, 0);
      }

      // After a full rebuild, immediately re-apply kinematics from the current joint refs.
      // This ensures pivot groups / zero-offset translations are reconstructed deterministically
      // (avoids "angles look right but links pulled apart" after physics stop).
      try {
        if (!isPhysicsRunning) {
          const storeNow = getSceneState();
          const jointsNow = storeNow.joints || {};
          Object.values(jointObjectsRef.current || {}).forEach((entry) => {
            const { bodyGroup, joint, initialRot, initialPos, parentRot } = entry || {};
            if (!bodyGroup || !joint) return;
            const freshJoint = jointsNow[joint.uuid] || joint;
            const kind = String(freshJoint.kind || "").toLowerCase();
            if (kind === "free") return;
            if (kind === "ball") {
              const quat = freshJoint.quat;
              if (Array.isArray(quat) && quat.length === 4) {
                applyJointValue(bodyGroup, freshJoint, quat, initialRot, initialPos, parentRot);
              }
              return;
            }
            const v = Number(freshJoint.ref);
            const value = Number.isFinite(v) ? v : 0;
            applyJointValue(bodyGroup, freshJoint, value, initialRot, initialPos, parentRot);
          });

          // Optional debug dump: helps diagnose "pulled apart" spacing issues.
          logKinematicsAfterRebuild({ jointObjectsRef, storeNow });
        }
      } catch (error) {
        logger.warn("[Scene] Failed to re-apply kinematics after rebuild", error);
      }

      // Debug: Check if joint objects were created
      // Focus: summary of joint objects created
      devLog("[JointDebug] Joint objects:", Object.keys(jointObjectsRef.current));

      publishSceneObjects(sceneObjectsRef, setSceneObjects);

      // Ensure position pick marker is hidden after scene is built
      hidePosPickMarker(sceneRef.current);

      // Compute joint marker sizes
      scheduleJointMarkerSizing({
        applyJointMarkerSizes,
        jointMarkerScalePercent,
        newFileMapSig,
        newStructureSig,
        sceneRef,
      });

      // Update structure signature after full rebuild
      updateSceneRebuildRefs({
        bodyTree,
        bodyTreeStructureSignatureRef,
        currentFileKeys,
        fileMapSignatureRef,
        initialBuildDoneRef,
        newFileMapSig,
        newStructureSig,
        previousBodiesRef,
        previousFileMapKeysRef,
      });
      // Mark structure-change signal as handled (we did a full rebuild).
      lastHandledStructureChangeTickRef.current = structureChangeTick;

      // Update the scaling factor to ensure correct rendering
    }
  }, [
    rebuildBodyTree,
    rebuildStructureSig,
    hasWorldLevelSitesForRebuild,
    fileMap,
    useYUp,
    structureChangeTick,
    jointKindsSignature,
  ]);
}
